import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowForward, IoTimeOutline } from "react-icons/io5";
import { projectsApi, Project } from "../../services/projectsApi";
import { userMapsApi, LoadGeometriesResponse } from "../../services/userMapsApi";
import { realtimeApi } from "../../services/realtimeApi";
import { projectRealtimeAvailability } from "../../utils/realtimeEnv";
import Spinner from "../common/Spinner";

const ArrowIcon = IoArrowForward as any;
const ClockIcon = IoTimeOutline as any;

// An area counts as reporting if it has a sample inside this window. The
// backend samples every ~10 min, so 30 min tolerates one missed slot without
// flapping, and anything older is not "live" by any reasonable reading.
const REPORTING_WINDOW_MS = 30 * 60 * 1000;

// Per-card monitoring state, derived by counting ids from two endpoints —
// nothing modelled here. `reporting` is null when the realtime call failed,
// in which case we only claim what Firestore says (enabled) and not data.
type CardStatus = { areas: number; enabled: number; reporting: number | null };

const StatusPill: React.FC<{ status: CardStatus }> = ({ status }) => {
  const { areas, enabled, reporting } = status;
  if (reporting != null && reporting > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-ok-soft px-2.5 py-1 text-[11px] font-medium text-ok-ink">
        <span className="h-1.5 w-1.5 rounded-full bg-ok animate-pulse" />
        Live · {reporting} of {areas} reporting
      </span>
    );
  }
  if (enabled > 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-warn-soft px-2.5 py-1 text-[11px] font-medium text-warn-ink">
        <span className="h-1.5 w-1.5 rounded-full bg-warn" />
        {reporting == null ? `Enabled · ${enabled} of ${areas}` : "Enabled · no data yet"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-[11px] font-medium text-ink-subtle">
      Not monitoring
    </span>
  );
};

// "Dashboards" = your saved projects, opened straight into their product.
// Today the only product with a project dashboard is Real Time, so Open goes
// to /realtime/project. Generalise when Activities/ODM gain project views.
const DashboardsHome: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastOpened, setLastOpened] = useState<string | null>(null);
  const [monitored, setMonitored] = useState<Set<string>>(new Set());
  const [reporting, setReporting] = useState<Set<string> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const now = Date.now();
      const [projs, prefs, geo, live] = await Promise.all([
        projectsApi.list(),
        projectsApi.getPreferences(),
        userMapsApi.loadGeometries().catch(() => ({ maps: [] as LoadGeometriesResponse["maps"] })),
        // One fleet-wide call: which areas have a sample in the last 30 min.
        // Fails open to null — a card must never say "no data" because this
        // request failed.
        realtimeApi
          .getRealtimeData({
            datetimeFrom: new Date(now - REPORTING_WINDOW_MS).toISOString(),
            datetimeTo: new Date(now).toISOString(),
          })
          .then((d) => new Set(d.rows.map((r) => r.target_id)))
          .catch(() => null),
      ]);
      setMonitored(new Set(geo.maps.filter((m) => m.is_monitored).map((m) => m.id)));
      setReporting(live);
      // Projects live in the shared store (all envs), so show only those whose
      // realtime data is readable here — inferred from member areas' bqProjectId.
      const bqById = new Map(geo.maps.map((m) => [m.id, m.bqProjectId]));
      setProjects(projs.filter((p) => projectRealtimeAvailability(p.areaIds, bqById) === "here"));
      setLastOpened(prefs.lastDashboardId ?? null);
    } catch (err) {
      console.warn("Could not load dashboards:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const open = (p: Project) =>
    navigate(`/realtime/project?project=${encodeURIComponent(p.id)}`);

  const statusOf = (p: Project): CardStatus => ({
    areas: p.areaIds.length,
    enabled: p.areaIds.filter((id) => monitored.has(id)).length,
    reporting: reporting ? p.areaIds.filter((id) => reporting.has(id)).length : null,
  });

  // Float the last-viewed dashboard to the top ("resume where you left off").
  const ordered = [...projects].sort((a, b) =>
    a.id === lastOpened ? -1 : b.id === lastOpened ? 1 : 0
  );

  return (
    <div className="w-full">
      <div className="mb-10">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
          Workbench
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
          Your dashboards
        </h1>
        <p className="text-lg text-ink-muted max-w-xl">
          Jump straight back into the areas you track.
        </p>
      </div>

      {loading ? (
        <div className="py-16">
          <Spinner />
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-paper-300 bg-surface py-16 text-center">
          <p className="text-base text-ink-muted">No dashboards yet.</p>
          <p className="mt-1 text-sm text-ink-faint">
            Select areas in{" "}
            <button
              className="font-medium text-brand-600 hover:underline"
              onClick={() => navigate("/monitor-areas-select")}
            >
              Monitor Areas
            </button>{" "}
            and choose “Save as project” to track them here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {ordered.map((p) => {
            const isLast = p.id === lastOpened;
            const status = statusOf(p);
            // Same muted surface the product cards use for gated products, but
            // still clickable — open it and enable areas from the dashboard.
            const surface = status.enabled > 0 ? "bg-surface" : "bg-paper-100/60";
            return (
              <button
                key={p.id}
                onClick={() => open(p)}
                className={`group relative overflow-hidden rounded-3xl border border-paper-300/70 ${surface} p-7 text-left shadow-soft transition-all duration-300 ease-brand hover:-translate-y-1 hover:shadow-card-hover hover:border-brand-500/40`}
              >
                {/* Decorative gradient orb (matches product cards) */}
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br from-brand-500/15 to-brand-mint/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative flex h-full flex-col">
                  <div className="mb-6 flex items-start justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center rounded-full bg-paper-200 px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest text-ink-subtle">
                        Real Time
                      </span>
                      <StatusPill status={status} />
                    </div>
                    {isLast && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700">
                        <ClockIcon className="h-3.5 w-3.5" /> Last viewed
                      </span>
                    )}
                  </div>
                  <h2 className="font-display text-2xl font-bold text-ink mb-1">
                    {p.name}
                  </h2>
                  <p className="text-ink-muted mb-6">
                    {p.areaIds.length} area{p.areaIds.length === 1 ? "" : "s"}
                  </p>
                  <div className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600 transition-all duration-200 group-hover:gap-3">
                    Open dashboard
                    <ArrowIcon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardsHome;
